import { useState } from 'react';

import { APIRequestParamNames } from '@src/types/apiRequestParamNames';
import { SorteringType } from '@src/types/minhelseentities';
import { URLQueryParamNames } from '@src/types/urlQueryParamNames';
import { ASC } from '@src/types/verktoyFilterTypes';
import { getSorteringQueryMaps } from '@utils/queryParamUtil';
import { removeTralingAndEmptyParamFromUrl } from '@utils/utils';

import { convertFromStringToBool } from './useFilterObject';

/**
 ** Oppdaterer både React-state og URL for et gitt query-param.
 ** Håndterer logikk for å sette eller fjerne parameteret fra URL-en,
 ** og sørger for at endringer i inputverdier synkroniseres med URL, query params og filterinnstillinger begge veier.
 */
const useQueryParam = (key: string, getQuery: URLSearchParams): [string | undefined, (val: string) => void] => {
  const [query, setQuery] = useState(getQuery.get(key) || undefined);

  const updateUrl = (newVal: string): void => {
    setQuery(newVal);
    let shouldSet = false;

    switch (key.toLowerCase()) {
      case URLQueryParamNames.StotterAnonymOppstart.toLowerCase():
        shouldSet = newVal.trim() !== '' && convertFromStringToBool(newVal);
        break;
      case URLQueryParamNames.Sok.toLowerCase():
        shouldSet = typeof newVal === 'string' && newVal !== '';
        break;
      case URLQueryParamNames.Sortering:
      case URLQueryParamNames.SorterStigende:
        shouldSet = newVal?.trim() !== '' && newVal !== undefined && newVal !== null;
        break;
    }

    let query = getQuery;
    if (shouldSet) {
      query = settKey(getQuery, key, newVal);
    } else {
      query = slettKey(getQuery, key);
    }
    if (typeof window !== 'undefined') {
      updateUrlWithParams(query);
    }
  };

  return [query, updateUrl];
};

export default useQueryParam;

const updateUrlWithParams = (params: URLSearchParams): void => {
  const { protocol, pathname, host } = window.location;
  fjernSorterstigendeParamNaarDefaultSortering(params);
  const query = params.toString();
  const newUrl = removeTralingAndEmptyParamFromUrl(query ? `${protocol}//${host}${pathname}?${query}` : `${protocol}//${host}${pathname}`);
  window.history.replaceState({}, '', newUrl);
};

const slettKey = (params: URLSearchParams, key: string): URLSearchParams => {
  if (!erSortering(key)) {
    params.delete(key);
  }
  if (key === URLQueryParamNames.SorterStigende) {
    params.delete(URLQueryParamNames.Sortering);
  }
  return params;
};

const settKey = (params: URLSearchParams, key: string, value: string): URLSearchParams => {
  params.delete(key);
  params.append(key, value);
  return params;
};

const erSortering = (key: string): boolean => {
  return key === URLQueryParamNames.Sortering || key === URLQueryParamNames.SorterStigende;
};

/* Forklaring: Hvis sortering er satt til default (VerktoyNavn) og sorterStigende er true (default),
/* så skal ikke sorterStigende være med i URLen. ( gjelder også API request, selv om ikke teknisk feil å sende med ekstra info )
/* Dette for å holde URLen så ren som mulig. Back end vil by default sortere ascending hvis ikke annet er spesifisert*/
const fjernSorterstigendeParamNaarDefaultSortering = (params: URLSearchParams): void => {
  if (params.has(URLQueryParamNames.Sortering)) {
    const sortering = getSorteringQueryMaps();
    const erSortering =
      params.get(URLQueryParamNames.Sortering) === sortering[SorteringType.VerktoyNavn].name ||
      params.get(APIRequestParamNames.Sortering) === sortering[SorteringType.VerktoyNavn].name;

    const erSorterStigende =
      params.get(URLQueryParamNames.SorterStigende) === ASC || params.get(APIRequestParamNames.Sorterstigende) === ASC;

    if (erSortering && erSorterStigende) {
      params.delete(URLQueryParamNames.SorterStigende);
      params.delete(APIRequestParamNames.Sorterstigende);
    }
  }
};
